import React, { useState, useEffect, useRef } from 'react';
import { searchTeams } from '../data/teamSearchData';
import theme from '../data/theme';

const TeamSearchInput = ({ selectedTeam, onTeamSelect, isMobile, isSmallMobile }) => {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const suggestionsRef = useRef(null);
  const inputRef = useRef(null);

  // Update suggestions as user types
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    
    if (value.trim() === '') {
      setSuggestions([]);
      setShowSuggestions(false);
    } else {
      const results = searchTeams(value);
      setSuggestions(results.slice(0, 8)); // Limit to 8 suggestions
      setShowSuggestions(true);
      setHighlightedIndex(0);
    }
  };

  // Handle team selection from dropdown
  const handleSelectTeam = (team) => {
    setInputValue(team.displayText);
    onTeamSelect(team);
    setShowSuggestions(false);
  };

  // Keyboard navigation for suggestions
  const handleKeyDown = (e) => {
    if (!showSuggestions) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (suggestions[highlightedIndex]) {
          handleSelectTeam(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowSuggestions(false);
        break;
      default:
        break;
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const inputPadding = isMobile ? '1rem' : '1.2rem';
  const fontSize = isMobile ? '1rem' : '1.1rem';

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <label
        style={{
          display: 'block',
          fontWeight: 700,
          fontSize: isMobile ? '1rem' : '1.15rem',
          color: theme.colors.text.primary,
          marginBottom: '1rem',
          textAlign: 'center'
        }}
      >
        🔍 Search for a Team
      </label>

      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => inputValue && setShowSuggestions(true)}
        placeholder="e.g., Manchester United, Barcelona, or create custom..."
        style={{
          width: '100%',
          padding: inputPadding,
          borderRadius: theme.radii.input,
          border: `2px solid ${theme.colors.border.default}`,
          fontSize: fontSize,
          background: `linear-gradient(135deg, ${theme.colors.background.base} 0%, ${theme.colors.background.section} 100%)`,
          color: theme.colors.text.primary,
          fontWeight: 600,
          transition: 'all 0.3s ease',
          minHeight: '50px',
          boxSizing: 'border-box',
          textAlign: 'left'
        }}
        onFocus={(e) => {
          e.target.style.borderColor = theme.colors.border.focus;
          e.target.style.boxShadow = `0 0 0 3px ${theme.colors.shadow.soft}`;
        }}
        onBlur={(e) => {
          e.target.style.borderColor = theme.colors.border.default;
          e.target.style.boxShadow = 'none';
        }}
      />

      {/* Autocomplete Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: theme.colors.background.base,
            border: `1px solid ${theme.colors.border.default}`,
            borderTop: 'none',
            borderRadius: `0 0 ${theme.radii.input} ${theme.radii.input}`,
            maxHeight: '300px',
            overflowY: 'auto',
            zIndex: 100,
            boxShadow: `0 8px 16px ${theme.colors.shadow.soft}`,
            marginTop: '-2px'
          }}
        >
          {suggestions.map((team, index) => (
            <div
              key={`${team.nation}-${team.league}-${team.name}`}
              onClick={() => handleSelectTeam(team)}
              style={{
                padding: '0.75rem 1rem',
                cursor: 'pointer',
                backgroundColor:
                  index === highlightedIndex
                    ? theme.colors.background.section
                    : 'transparent',
                borderBottom:
                  index < suggestions.length - 1
                    ? `1px solid ${theme.colors.border.default}`
                    : 'none',
                transition: 'background-color 0.2s ease',
                userSelect: 'none'
              }}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              <div style={{ color: theme.colors.text.heading, fontWeight: 600 }}>
                {team.name}
              </div>
              <div
                style={{
                  fontSize: '0.85rem',
                  color: theme.colors.text.muted,
                  marginTop: '0.25rem'
                }}
              >
                {team.league} • {team.nation}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No results message */}
      {showSuggestions && inputValue && suggestions.length === 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: theme.colors.background.base,
            border: `1px solid ${theme.colors.border.default}`,
            borderTop: 'none',
            borderRadius: `0 0 ${theme.radii.input} ${theme.radii.input}`,
            padding: '1rem',
            textAlign: 'center',
            color: theme.colors.text.muted,
            zIndex: 100,
            marginTop: '-2px'
          }}
        >
          No teams found. Create a custom team by clicking below.
        </div>
      )}
    </div>
  );
};

export default TeamSearchInput;
